import React from "react";
import { Row, Col, Button, MessageBanner } from "@folio/stripes/components";
import { useFieldArray, useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { firstOnix } from "../../utils/selectMappers";
import { useOnixOptions } from "../../hooks/title/useOnixOptions";
import { useItemEditor } from "../../hooks/title/useItemEditor";
import { useContentRows } from "../../utils/toContentRows";
import { FormSectionModal } from "./FormSectionModal";
import { useFieldArrayErrors } from "../../hooks/useFieldArrayErrors";
import { MAIN_NAME_TYPE_VALUE } from "../../utils/titleDisplay";
import { EntityRowList } from "./EntityRowList";
import css from "./FormModal.module.css";
import rowCss from "./EntityRow.module.css";
var TitleNamesForm = function () {
    var _a;
    var formatMessage = useIntl().formatMessage;
    var _b = useFormContext(), control = _b.control, getValues = _b.getValues;
    var errorMessages = useFieldArrayErrors("names");
    var _c = useFieldArray({ control: control, name: "names" }), nameFields = _c.fields, appendName = _c.append, updateName = _c.update, removeName = _c.remove;
    var _d = useOnixOptions(), nameTypeTable = _d.nameTypeTable, nameTypeLoading = _d.nameTypeLoading, nameTypeStatus = _d.nameTypeStatus;
    var _e = useItemEditor({
        appendItem: appendName,
        updateItem: updateName,
        createEmpty: function () { return ({
            type: firstOnix(nameTypeTable),
            name: "",
        }); },
    }), isOpen = _e.isOpen, isEditing = _e.isEditing, editingIndex = _e.editingIndex, draft = _e.draft, setDraft = _e.setDraft, openEditor = _e.openEditor, closeEditor = _e.closeEditor, saveItem = _e.saveItem;
    var contentData = useContentRows(nameFields);
    var renderName = function (row) {
        var _a;
        var details = [
            {
                labelId: "ui-br-agency.title.names.subtitle.label",
                value: row.subtitle,
            },
            {
                labelId: "ui-br-agency.title.names.partNumber.label",
                value: row.partNumber,
            },
            {
                labelId: "ui-br-agency.title.names.partName.label",
                value: row.partName,
            },
        ].filter(function (d) { return d.value != null && String(d.value).trim() !== ""; });
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { className: rowCss.entryType }, ((_a = row.type) === null || _a === void 0 ? void 0 : _a.description) || "-"),
            React.createElement("div", { className: rowCss.entryTitle }, row.name || "-"),
            details.map(function (d) { return (React.createElement("div", { key: d.labelId, className: rowCss.entrySecondary },
                formatMessage({ id: d.labelId }),
                ": ",
                d.value)); })));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Row, null,
            React.createElement(Col, { xs: 12 }, nameFields.length > 0 ? (React.createElement(EntityRowList, { data: contentData, renderContent: renderName, onEdit: function (row) {
                    return openEditor(getValues("names.".concat(row._index)), row._index);
                }, onDelete: function (row) { return removeName(row._index); }, canDelete: function () { return nameFields.length > 1; } })) : (React.createElement("div", { className: css.emptyState },
                React.createElement(FormattedMessage, { id: "ui-br-agency.title.names.empty" }))))),
        errorMessages.length > 0 && (React.createElement(Row, { "data-error-field": "names" },
            React.createElement(Col, { xs: 12 },
                React.createElement(MessageBanner, { type: "error", className: css.tableErrorBanner }, errorMessages.map(function (msg) { return (React.createElement("div", { key: msg }, msg)); }))))),
        React.createElement(FormSectionModal, { open: isOpen, isEditing: isEditing, draft: draft, onDraftChange: setDraft, onSave: saveItem, onClose: closeEditor, addLabelId: "ui-br-agency.title.names.form.add.title", editLabelId: "ui-br-agency.title.names.form.edit.title", size: "large", fields: [
                {
                    key: "type",
                    labelId: "ui-br-agency.title.names.type.label",
                    type: "onix",
                    required: true,
                    dataOptions: (_a = nameTypeTable === null || nameTypeTable === void 0 ? void 0 : nameTypeTable.onixCodetableItems) !== null && _a !== void 0 ? _a : [],
                    loading: nameTypeLoading,
                    codetableStatus: nameTypeStatus,
                },
                {
                    key: "name",
                    labelId: "ui-br-agency.title.names.name.label",
                    type: "text",
                    required: true,
                },
                {
                    key: "subtitle",
                    labelId: "ui-br-agency.title.names.subtitle.label",
                    type: "text",
                },
                {
                    key: "partNumber",
                    labelId: "ui-br-agency.title.names.partNumber.label",
                    type: "text",
                },
                {
                    key: "partName",
                    labelId: "ui-br-agency.title.names.partName.label",
                    type: "text",
                },
            ], validate: function (d) {
                var _a, _b, _c;
                if (!((_a = d.type) === null || _a === void 0 ? void 0 : _a.value) || !((_b = d.name) === null || _b === void 0 ? void 0 : _b.trim())) {
                    return false;
                }
                // A title may contain only one main title name (type "01"). Block
                // adding/setting a second one; editing the existing main name is fine.
                if (d.type.value === MAIN_NAME_TYPE_VALUE) {
                    var existingNames = ((_c = getValues("names")) !== null && _c !== void 0 ? _c : []);
                    var hasOtherMainName = existingNames.some(function (n, i) { var _a; return i !== editingIndex && ((_a = n.type) === null || _a === void 0 ? void 0 : _a.value) === MAIN_NAME_TYPE_VALUE; });
                    if (hasOtherMainName) {
                        return "ui-br-agency.title.validation.mainName.onlyOne";
                    }
                }
                return true;
            } }),
        React.createElement(Row, { className: css.actions },
            React.createElement(Col, { xs: 12 },
                React.createElement(Button, { buttonStyle: "primary", onClick: function () { return openEditor(); } },
                    React.createElement(FormattedMessage, { id: "ui-br-agency.title.form.add.name" }))))));
};
export default TitleNamesForm;
